import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

// Gauge Component - Blue, Beige, Gray theme
const GaugeMeter = ({ value, max, label, color, icon }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const strokeDasharray = `${percentage * 2.51327} ${251.327 - percentage * 2.51327}`;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-32 h-32 transform -rotate-90">
          <circle cx="64" cy="64" r="56" stroke="#334155" strokeWidth="12" fill="none" />
          <circle 
            cx="64" cy="64" r="56" 
            stroke={color} 
            strokeWidth="12" 
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1" style={{ background: color }}>
            <span className="text-white text-sm">{icon}</span>
          </div>
          <span className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{Math.round(percentage)}%</span>
        </div>
      </div>
      <p className="mt-2 text-sm font-medium text-gray-600 text-center">{label}</p>
    </div>
  );
};

// Score Badge Component
const ScoreBadge = ({ score, label, size = 'normal' }) => {
  const getColor = () => {
    if (label === 'ESG') return score >= 80 ? 'bg-sky-100 text-sky-700' : score >= 60 ? 'bg-sky-50 text-sky-600' : 'bg-gray-100 text-gray-600';
    if (label === 'Mkt') return score >= 80 ? 'bg-blue-100 text-blue-700' : score >= 60 ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600';
    return score >= 80 ? 'bg-purple-100 text-purple-700' : score >= 60 ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-600';
  };
  
  if (size === 'small') {
    return <span className={`px-1.5 py-0.5 ${getColor()} text-[10px] rounded font-semibold`}>{label} {score}</span>;
  }
  return <span className={`px-2 py-1 ${getColor()} text-xs rounded-lg font-semibold`}>{label} {score}</span>;
};

const AllCampaigns = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, analyticsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/overview`, { withCredentials: true })
      ]);
      
      setUser(userRes.data);
      setStats(analyticsRes.data);
      
      // Get all campaigns
      const campaignsRes = await axios.get(`${BACKEND_URL}/api/campaigns`, { withCredentials: true });
      setCampaigns(campaignsRes.data || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      navigate('/');
    }
  };

  // Generate timeline data from campaigns
  const generateTimelineData = () => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const data = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = monthNames[month.getMonth()];
      
      // Simulate growth data based on campaigns
      const baseRaised = campaigns.reduce((sum, c) => sum + (c.raised_amount || 0), 0);
      const growth = baseRaised * (0.1 + Math.random() * 0.15) * ((6 - i) / 6);
      
      data.push({
        month: monthKey,
        raised: Math.round(growth),
        donations: Math.round(10 + Math.random() * 20 * ((6 - i) / 6)),
        campaigns: Math.max(1, campaigns.filter(c => c.status === 'active').length - i + Math.floor(Math.random() * 2))
      });
    }
    
    return data;
  };

  // Category distribution for pie chart
  const getCategoryData = () => {
    const categories = {};
    campaigns.forEach(c => {
      const cat = c.charity_name?.split(' ')[0] || 'Other';
      categories[cat] = (categories[cat] || 0) + (c.raised_amount || 0);
    });
    
    return Object.entries(categories).slice(0, 5).map(([name, value]) => ({
      name: name.length > 10 ? name.slice(0, 10) + '...' : name,
      value
    }));
  };

  // Brand Colors: Blue, Beige, Gray palette
  const COLORS = ['#2563EB', '#38BDF8', '#6366F1', '#818CF8', '#64748B'];

  // Filter and sort campaigns
  const filteredCampaigns = campaigns
    .filter(c => filterStatus === 'all' || c.status === filterStatus)
    .sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === 'created_at') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }
      if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

  // Edit campaign handlers
  const openEditModal = (campaign) => {
    setEditingCampaign(campaign);
    setEditForm({
      title: campaign.title,
      description: campaign.description,
      goal_amount: campaign.goal_amount,
      status: campaign.status,
      employer_match_enabled: campaign.employer_match_enabled || false,
      employer_match_ratio: campaign.employer_match_ratio || 1,
      employer_match_cap: campaign.employer_match_cap || 5000
    });
    setShowEditModal(true);
  };

  const saveEditedCampaign = async () => {
    if (!editingCampaign) return;
    setSaving(true);
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/campaigns/${editingCampaign.campaign_id}`,
        editForm,
        { withCredentials: true }
      );
      
      // Refresh campaigns
      const campaignsRes = await axios.get(`${BACKEND_URL}/api/campaigns`, { withCredentials: true });
      setCampaigns(campaignsRes.data || []);
      
      setShowEditModal(false);
      setEditingCampaign(null);
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Campaign Title', 'Charity', 'Status', 'Goal', 'Raised', 'Progress', 'ESG Score', 'Marketing Score', 'Brand Score', 'Employer Match', 'Created'];
    const rows = campaigns.map(c => [
      c.title,
      c.charity_name,
      c.status,
      c.goal_amount,
      c.raised_amount || 0,
      `${Math.round(((c.raised_amount || 0) / c.goal_amount) * 100)}%`,
      c.esg_score || 'N/A',
      c.marketing_score || 'N/A',
      c.brand_score || 'N/A',
      c.employer_match_enabled ? `${c.employer_match_ratio}:1` : 'No',
      new Date(c.created_at).toLocaleDateString()
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaigns_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Calculate totals
  const totalRaised = campaigns.reduce((sum, c) => sum + (c.raised_amount || 0), 0);
  const totalGoal = campaigns.reduce((sum, c) => sum + (c.goal_amount || 0), 0);
  const avgESG = campaigns.length > 0 ? Math.round(campaigns.reduce((sum, c) => sum + (c.esg_score || 75), 0) / campaigns.length) : 0;
  const avgMarketing = campaigns.length > 0 ? Math.round(campaigns.reduce((sum, c) => sum + (c.marketing_score || 70), 0) / campaigns.length) : 0;
  const avgBrand = campaigns.length > 0 ? Math.round(campaigns.reduce((sum, c) => sum + (c.brand_score || 72), 0) / campaigns.length) : 0;
  const withEmployerMatch = campaigns.filter(c => c.employer_match_enabled).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  const timelineData = generateTimelineData();
  const categoryData = getCategoryData();

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-canvas)' }}>
      {/* Header - Blue, Beige, Gray */}
      <header className="shadow-md px-6 py-4" style={{ background: 'var(--bg-canvas)', borderBottom: '2px solid #0C81E4' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" style={{ color: 'var(--text-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>All Campaigns & Reports</h1>
              <p className="text-sm text-gray-500">Comprehensive view of all your impact campaigns</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportToCSV}
              className="px-4 py-2 text-white rounded-lg font-medium flex items-center gap-2 transition-colors hover:opacity-90"
              style={{ background: 'var(--brand-primary)' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Cards - Blue, Beige, Gray Theme */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {/* Total Raised - Blue */}
          <div className="rounded-xl p-4 shadow-lg" style={{ background: 'var(--brand-primary)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">${totalRaised.toLocaleString()}</p>
                <p className="text-xs text-blue-200">Total Raised</p>
              </div>
            </div>
          </div>
          
          {/* Total Campaigns - Light Blue */}
          <div className="rounded-xl p-4 shadow-lg" style={{ background: '#0C81E4' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{campaigns.length}</p>
                <p className="text-xs text-blue-100">Total Campaigns</p>
              </div>
            </div>
          </div>
          
          {/* Active - Beige */}
          <div className="rounded-xl p-4 shadow-lg" style={{ background: 'var(--bg-raised)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--brand-primary)' }}>
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{campaigns.filter(c => c.status === 'active').length}</p>
                <p className="text-xs text-gray-600">Active</p>
              </div>
            </div>
          </div>
          
          {/* With Match - Gray */}
          <div className="rounded-xl p-4 shadow-lg bg-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#64748b' }}>
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{withEmployerMatch}</p>
                <p className="text-xs text-gray-500">With Match</p>
              </div>
            </div>
          </div>
          
          {/* Goal Progress - Blue Gradient */}
          <div className="rounded-xl p-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #38BDF8 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{totalGoal > 0 ? Math.round((totalRaised / totalGoal) * 100) : 0}%</p>
                <p className="text-xs text-blue-100">Goal Progress</p>
              </div>
            </div>
          </div>
          
          {/* Lives Impacted - Beige */}
          <div className="rounded-xl p-4 shadow-lg" style={{ background: 'var(--bg-raised)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--brand-primary)' }}>
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats?.total_impact?.toLocaleString() || 0}</p>
                <p className="text-xs" style={{ color: 'var(--text-primary)' }}>Lives Impacted</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'campaigns', 'analytics'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 rounded-lg font-medium capitalize transition-all ${
                activeTab === tab 
                  ? 'text-white shadow-lg' 
                  : 'bg-white text-gray-600 hover:bg-gray-50 border'
              }`}
              style={activeTab === tab ? { background: 'var(--brand-primary)' } : {}}
            >
              {tab === 'overview' ? 'Overview' : tab === 'campaigns' ? 'All Campaigns' : 'Analytics'}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Gauge Meters Row - Blue, Beige, Gray Theme */}
            <div className="bg-white rounded-xl p-6 shadow-lg border-t-4" style={{ borderColor: '#0C81E4' }}>
              <h3 className="text-lg font-bold mb-6" style={{ color: 'var(--text-primary)' }}>Performance Gauges</h3>
              <div className="flex justify-around flex-wrap gap-6">
                <GaugeMeter 
                  value={totalRaised} 
                  max={totalGoal || 1} 
                  label="Overall Goal Progress" 
                  color="#2563EB"
                  icon="🎯"
                />
                <GaugeMeter 
                  value={avgESG} 
                  max={100} 
                  label="Average ESG Score" 
                  color="#38BDF8"
                  icon="🌱"
                />
                <GaugeMeter 
                  value={avgMarketing} 
                  max={100} 
                  label="Marketing Value" 
                  color="#0C81E4"
                  icon="📢"
                />
                <GaugeMeter 
                  value={avgBrand} 
                  max={100} 
                  label="Brand Fit Score" 
                  color="#818CF8"
                  icon="⭐"
                />
                <GaugeMeter 
                  value={campaigns.filter(c => c.status === 'active').length} 
                  max={campaigns.length || 1} 
                  label="Active Campaigns" 
                  color="#1E40AF"
                  icon="⚡"
                />
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Timeline Chart */}
              <div className="bg-white rounded-xl p-6 shadow-lg border-t-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Fundraising Timeline</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={timelineData}>
                    <defs>
                      <linearGradient id="colorRaised" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0C81E4" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                    <YAxis stroke="#6B7280" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                    <Tooltip 
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Raised']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                    />
                    <Area type="monotone" dataKey="raised" stroke="#2563EB" strokeWidth={2} fill="url(#colorRaised)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Category Distribution */}
              <div className="bg-white rounded-xl p-6 shadow-lg border-t-4" style={{ borderColor: '#0C81E4' }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Funds by Charity</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-3 mt-2">
                  {categoryData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-xs text-gray-600">{entry.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Growth Chart */}
            <div className="bg-white rounded-xl p-6 shadow-lg border-t-4" style={{ borderColor: 'var(--brand-accent)' }}>
              <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Campaign Growth Trends</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                  <YAxis stroke="#6B7280" fontSize={12} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                  <Bar dataKey="donations" fill="#2563EB" radius={[4, 4, 0, 0]} name="Donations" />
                  <Bar dataKey="campaigns" fill="#0C81E4" radius={[4, 4, 0, 0]} name="Campaigns" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* All Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="bg-white rounded-xl shadow-lg border-t-4 overflow-hidden" style={{ borderColor: 'var(--brand-primary)' }}>
            {/* Filters */}
            <div className="p-4 border-b flex items-center justify-between flex-wrap gap-3" style={{ background: 'var(--bg-canvas)' }}>
              <div className="flex items-center gap-3">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm bg-white"
                >
                  <option value="created_at">Date Created</option>
                  <option value="raised_amount">Amount Raised</option>
                  <option value="goal_amount">Goal Amount</option>
                  <option value="title">Title</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border rounded-lg text-sm bg-white hover:bg-gray-50"
                >
                  {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
                </button>
              </div>
              <p className="text-sm text-gray-500">
                Showing <span className="font-semibold">{filteredCampaigns.length}</span> campaigns
              </p>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ background: 'var(--brand-primary)' }}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Campaign</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Scores</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Employer Match</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCampaigns.map((campaign) => (
                    <tr key={campaign.campaign_id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <p className="font-semibold text-gray-900">{campaign.title}</p>
                        <p className="text-xs text-gray-500">{campaign.charity_name}</p>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          campaign.status === 'active' ? 'bg-green-100 text-green-700' :
                          campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="w-36">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-medium">${(campaign.raised_amount || 0).toLocaleString()}</span>
                            <span className="text-gray-500">/ ${(campaign.goal_amount || 0).toLocaleString()}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${campaign.status === 'active' ? 'bg-blue-600' : 'bg-gray-400'}`}
                              style={{ width: `${Math.min(((campaign.raised_amount || 0) / (campaign.goal_amount || 1)) * 100, 100)}%` }}
                            />
                          </div>
                          <p className="text-xs text-blue-600 font-medium mt-1">
                            {Math.round(((campaign.raised_amount || 0) / (campaign.goal_amount || 1)) * 100)}%
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1">
                          <ScoreBadge score={campaign.esg_score || 75} label="ESG" size="small" />
                          <ScoreBadge score={campaign.marketing_score || 70} label="Mkt" size="small" />
                          <ScoreBadge score={campaign.brand_score || 72} label="Brand" size="small" />
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {campaign.employer_match_enabled ? (
                          <div>
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                              </svg>
                              {campaign.employer_match_ratio || 1}:1 Match
                            </span>
                            <p className="text-xs text-gray-500 mt-1">Cap: ${campaign.employer_match_cap?.toLocaleString() || 'N/A'}</p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No match</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-gray-600">
                          {new Date(campaign.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button 
                            onClick={() => openEditModal(campaign)}
                            className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => navigate('/dashboard')}
                            className="px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg text-xs font-medium transition-colors"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredCampaigns.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">No campaigns found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* ESG Performance - Blue, Beige, Gray Theme */}
            <div className="bg-white rounded-xl p-6 shadow-lg border-t-4" style={{ borderColor: 'var(--brand-primary)' }}>
              <h3 className="text-lg font-bold mb-6" style={{ color: 'var(--text-primary)' }}>ESG Performance Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="rounded-xl p-6" style={{ background: 'linear-gradient(135deg, var(--bg-raised) 0%, var(--bg-subtle) 100%)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Average ESG Score</span>
                    <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{avgESG}</span>
                  </div>
                  <div className="w-full rounded-full h-3" style={{ background: 'var(--bg-canvas)' }}>
                    <div className="h-3 rounded-full transition-all" style={{ width: `${avgESG}%`, background: 'var(--brand-primary)' }} />
                  </div>
                  <p className="text-xs mt-2" style={{ color: 'var(--text-primary)' }}>Environmental, Social & Governance</p>
                </div>
                
                <div className="rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-blue-100">Marketing Value</span>
                    <span className="text-3xl font-bold text-white">{avgMarketing}</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-3">
                    <div className="bg-white h-3 rounded-full transition-all" style={{ width: `${avgMarketing}%` }} />
                  </div>
                  <p className="text-xs text-blue-200 mt-2">Brand visibility & PR potential</p>
                </div>
                
                <div className="rounded-xl p-6" style={{ background: 'linear-gradient(135deg, #38BDF8 0%, #2563EB 100%)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-blue-100">Brand Fit</span>
                    <span className="text-3xl font-bold text-white">{avgBrand}</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-3">
                    <div className="bg-white h-3 rounded-full transition-all" style={{ width: `${avgBrand}%` }} />
                  </div>
                  <p className="text-xs text-blue-100 mt-2">Strategic alignment score</p>
                </div>
              </div>
            </div>

            {/* Impact by Category - Blue, Beige, Gray Theme */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-xl p-6 shadow-lg" style={{ background: 'linear-gradient(135deg, var(--bg-raised) 0%, var(--bg-subtle) 100%)' }}>
                <h4 className="font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                  <span className="text-xl">🌱</span> Environmental Impact
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white/50 rounded-lg">
                    <span className="text-sm text-gray-700">Trees Planted (est.)</span>
                    <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{Math.round(totalRaised * 0.05)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/50 rounded-lg">
                    <span className="text-sm text-gray-700">CO2 Offset (tons)</span>
                    <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{Math.round(totalRaised * 0.001)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/50 rounded-lg">
                    <span className="text-sm text-gray-700">Environmental Campaigns</span>
                    <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{campaigns.filter(c => c.charity_name?.toLowerCase().includes('environment')).length || 0}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl p-6 shadow-lg" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #38BDF8 100%)' }}>
                <h4 className="font-bold text-white mb-4 flex items-center gap-2">
                  <span className="text-xl">💙</span> Social Impact
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white/10 rounded-lg">
                    <span className="text-sm text-blue-100">Students Supported (est.)</span>
                    <span className="font-bold text-white">{Math.round(totalRaised * 0.1)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/10 rounded-lg">
                    <span className="text-sm text-blue-100">Meals Provided (est.)</span>
                    <span className="font-bold text-white">{Math.round(totalRaised * 0.5)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white/10 rounded-lg">
                    <span className="text-sm text-blue-100">Lives Impacted</span>
                    <span className="font-bold text-white">{stats?.total_impact?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* UN SDG Alignment - Blue, Beige, Gray Theme */}
            <div className="bg-white rounded-xl p-6 shadow-lg border-t-4" style={{ borderColor: 'var(--brand-accent)' }}>
              <h4 className="font-bold mb-4" style={{ color: 'var(--text-primary)' }}>UN Sustainable Development Goals Alignment</h4>
              <div className="flex flex-wrap gap-2">
                {['SDG 1: No Poverty', 'SDG 3: Good Health', 'SDG 4: Quality Education', 'SDG 13: Climate Action', 'SDG 17: Partnerships'].map((sdg, idx) => (
                  <span 
                    key={sdg} 
                    className="px-4 py-2 rounded-full text-sm font-medium" 
                    style={{ 
                      background: idx % 2 === 0 ? 'var(--brand-primary)' : 'var(--bg-raised)', 
                      color: idx % 2 === 0 ? 'white' : 'var(--text-primary)' 
                    }}
                  >
                    {sdg}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Edit Campaign Modal */}
      {showEditModal && editingCampaign && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Edit Campaign</h2>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Goal Amount ($)</label>
                  <input
                    type="number"
                    value={editForm.goal_amount}
                    onChange={(e) => setEditForm({...editForm, goal_amount: parseFloat(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              
              {/* Employer Match Section */}
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-amber-600">👥</span>
                    <span className="font-semibold text-amber-900">Employer Match</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={editForm.employer_match_enabled}
                      onChange={(e) => setEditForm({...editForm, employer_match_enabled: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>
                
                {editForm.employer_match_enabled && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs text-amber-800 mb-1">Match Ratio</label>
                      <select
                        value={editForm.employer_match_ratio}
                        onChange={(e) => setEditForm({...editForm, employer_match_ratio: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white"
                      >
                        <option value="0.5">0.5:1 (50%)</option>
                        <option value="1">1:1 (100%)</option>
                        <option value="2">2:1 (200%)</option>
                        <option value="3">3:1 (300%)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-amber-800 mb-1">Cap per Employee ($)</label>
                      <input
                        type="number"
                        value={editForm.employer_match_cap}
                        onChange={(e) => setEditForm({...editForm, employer_match_cap: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveEditedCampaign}
                disabled={saving}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllCampaigns;
